/**
 * User-Friendly Error Message Mapping
 * Maps backend error messages to clearer, more concise frontend messages
 */

/**
 * Get user-friendly error message
 * @param {string} backendError - Error message from backend
 * @returns {string} - User-friendly error message
 */
export const getUserFriendlyError = (backendError) => {
  if (!backendError) {
    return "Something went wrong. Please try again.";
  }

  // Convert to lowercase for case-insensitive matching
  const error = backendError.toLowerCase();

  // Account Collision Errors
  if (error.includes("you already have an email associated")) {
    return "You already have an email on your account. Please use a different email or contact support.";
  }

  if (error.includes("you already have a mobile number associated")) {
    return "You already have a phone number on your account. Please use a different number or contact support.";
  }

  if (error.includes("cannot merge accounts as it would result in data loss")) {
    if (error.includes("mobile number")) {
      return "This email is already registered with another account.";
    }
    if (error.includes("email")) {
      return "This phone number is already registered with another account.";
    }
    return "Cannot merge accounts - this would cause data loss.";
  }

  // OTP Errors
  if (error.includes("invalid otp")) {
    return "Incorrect OTP. Please check and try again.";
  }

  if (error.includes("otp expired")) {
    return "OTP has expired. Please request a new one.";
  }

  if (error.includes("cannot merge account with itself")) {
    return "Cannot link the same account. Please try a different account.";
  }

  if (error.includes("cannot merge with an inactive account")) {
    return "This account is no longer active. Please contact support.";
  }

  if (error.includes("cannot merge accounts. you already have an email")) {
    return "You already have an email.";
  }

  if (error.includes("cannot merge accounts. you already have a mobile")) {
    return "You already have a phone number.";
  }

  if (error.includes("failed to merge accounts")) {
    return "Account merge failed. Please try again or contact support.";
  }

  // Authentication Errors
  if (error.includes("unauthorized")) {
    return "Session expired. Please login again.";
  }

  if (error.includes("forbidden")) {
    return "You don't have permission to perform this action.";
  }

  if (error.includes("user not found")) {
    return "Account not found. Please check your details.";
  }

  // Validation Errors
  if (error.includes("validation failed")) {
    return "Please check your information and try again.";
  }

  if (error.includes("bad request")) {
    return "Invalid request. Please try again.";
  }

  // Network/Generic Errors
  if (error.includes("network error") || error.includes("failed to fetch")) {
    return "Network error. Please check your connection and try again.";
  }

  if (error.includes("something went wrong")) {
    return "Something went wrong. Please try again.";
  }

  // Default: Return original message if no match found
  return backendError;
};

/**
 * Get error message with action suggestion
 * @param {string} backendError - Error message from backend
 * @returns {object} - { message: string, action: string }
 */
export const getErrorWithAction = (backendError) => {
  if (!backendError) {
    return {
      message: "Something went wrong.",
      action: "Please try again later."
    };
  }

  const error = backendError.toLowerCase();

  // Account Collision Errors with Actions
  if (error.includes("you already have an email associated")) {
    return {
      message: "You already have an email on your account.",
      action: "Try using a different email or contact support if you need help."
    };
  }

  if (error.includes("you already have a mobile number associated")) {
    return {
      message: "You already have a phone number on your account.",
      action: "Try using a different number or contact support if you need help."
    };
  }

  if (error.includes("cannot merge accounts as it would result in data loss")) {
    return {
      message: "Cannot merge these accounts.",
      action: "This would cause data loss. Please contact support for assistance."
    };
  }

  // OTP Errors with Actions
  if (error.includes("invalid otp")) {
    return {
      message: "Incorrect OTP.",
      action: "Please check the code and try again."
    };
  }

  if (error.includes("otp expired")) {
    return {
      message: "OTP has expired.",
      action: "Click 'Resend OTP' to get a new code."
    };
  }

  // Authentication Errors with Actions
  if (error.includes("unauthorized")) {
    return {
      message: "Your session has expired.",
      action: "Please login again to continue."
    };
  }

  // Default
  return {
    message: getUserFriendlyError(backendError),
    action: "Please try again or contact support if the problem persists."
  };
};

/**
 * Error message categories for styling/icons
 */
export const ERROR_CATEGORIES = {
  COLLISION: "collision",
  OTP: "otp",
  AUTH: "auth",
  VALIDATION: "validation",
  NETWORK: "network",
  GENERIC: "generic"
};

/**
 * Get error category
 * @param {string} backendError - Error message from backend
 * @returns {string} - Error category
 */
export const getErrorCategory = (backendError) => {
  if (!backendError) return ERROR_CATEGORIES.GENERIC;

  const error = backendError.toLowerCase();

  if (error.includes("already have") || error.includes("cannot merge") || error.includes("data loss")) {
    return ERROR_CATEGORIES.COLLISION;
  }

  if (error.includes("otp")) {
    return ERROR_CATEGORIES.OTP;
  }

  if (error.includes("unauthorized") || error.includes("forbidden") || error.includes("session")) {
    return ERROR_CATEGORIES.AUTH;
  }

  if (error.includes("validation") || error.includes("invalid")) {
    return ERROR_CATEGORIES.VALIDATION;
  }

  if (error.includes("network") || error.includes("connection")) {
    return ERROR_CATEGORIES.NETWORK;
  }

  return ERROR_CATEGORIES.GENERIC;
};

